import { FC } from 'react';

import { useTheme, useMediaQuery, Breakpoint } from '@mui/material';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';

import { theme } from '~styles/theme';

type ResponsiveSize = Partial<Record<Breakpoint, number | string>>;

export type AppIconProps = {
  component: any;
  gradient?: boolean;
  gradientType?: '1' | '2' | '3';
  size?: ResponsiveSize | number | string;
  color?: string;
  fillMode?: 'stroke' | 'fill';
} & Omit<SvgIconProps, 'color'>;

const useCurrentBreakpoint = (): Breakpoint => {
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  return 'xs';
};

const resolveResponsiveSize = (
  size: ResponsiveSize | number | string | undefined,
  current: Breakpoint
): number | string | undefined => {
  if (typeof size !== 'object' || size === null) return size;

  const order: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];
  const currentIndex = order.indexOf(current);

  for (let i = currentIndex; i >= 0; i--) {
    const key = order[i];
    if (size[key] != null) return size[key];
  }

  return undefined;
};

export const AppIcon: FC<AppIconProps> = ({
  component,
  gradient,
  gradientType = 1,
  size = 24,
  color,
  fillMode = 'stroke',
  ...props
}) => {
  const currentBreakpoint = useCurrentBreakpoint();
  const resolvedSize = resolveResponsiveSize(size, currentBreakpoint);

  const resolveColor = (input?: string): string | undefined => {
    if (!input) return undefined;
    if (input.startsWith('#') || input.startsWith('rgb')) return input;

    const fromTheme = input
      .split('.')
      .reduce((acc: any, key: string) => acc?.[key], theme.palette);

    return typeof fromTheme === 'string' ? fromTheme : input;
  };

  const resolvedColor = resolveColor(color);

  // Определяем цвета градиента
  const gradient1 = ['#9A56EE', '#006AFF'];
  const gradient2 = ['#027bfe', '#2fcc71'];
  const gradientColors = gradientType === '1' ? gradient1 : gradient2;

  const gradientColor = gradient
    ? `url(#app-icon-gradient-${gradientType})`
    : resolvedColor || 'currentColor';

  return (
    <SvgIcon
      component={component}
      inheritViewBox
      {...props}
      sx={{
        ...props.sx,
        fontSize: `${resolvedSize}px !important`,
        fill: fillMode === 'fill' ? 'currentColor' : 'none',
        stroke: fillMode === 'stroke' ? 'currentColor' : 'none',

        '& path, & circle, & rect, & line, & polyline, & polygon': {
          fill: fillMode === 'fill' ? gradientColor : 'none',
          stroke: fillMode === 'stroke' ? gradientColor : 'none',
        },
      }}
    />
  );

  return (
    <SvgIcon
      component={component}
      inheritViewBox
      {...props}
      sx={{
        ...props.sx,
        fontSize: `${resolvedSize}px !important`,
        fill: 'none',
        stroke: 'currentColor',
        '& path': {
          fill: 'none',
          stroke: gradient
            ? `url(#app-icon-gradient-${gradientType})`
            : resolvedColor || 'currentColor',
        },
        '& *': {
          fill: 'none',
          stroke: resolvedColor || 'currentColor',
        },
      }}
    />
  );
};
